// covered-by: frontends/carrie/src/utils/__tests__/enterpriseSwitch.test.ts
// covered-by: frontends/carrie/src/utils/queries/__tests__/enterprises.test.tsx
import { type SwitchEnterpriseResponse } from "@mg/schemas/src/prince/auth";

// The step arm and its guard live in a leaf module because `services/auth`
// classifies the switch response with the same guard, and this module imports
// that service.
import {
  isEnterpriseMfaStep,
  mfaOptionsOf,
  type EnterpriseMfaStep,
} from "./enterpriseMfaStep";
import { openEnterpriseMfaSwitch } from "./slices/ui";
import { type AppDispatch } from "./store";

import { switchEnterprise } from "../services/auth";

/** Whether the switch completed, or is waiting on the MFA step. */
export type EnterpriseSwitchOutcome =
  | { mfaRequired: true }
  | { mfaRequired: false; token: string };

/**
 * The one path every enterprise switch goes through.
 *
 * The MFA step has no token — dispatching `login` with it would decode
 * `undefined` and throw — so this parks the step in the UI slice, which opens
 * `AuthenticationDialog` on its MFA step. Verifying the code there calls the
 * same `/workos-mfa` the login path uses; christo mints that token for
 * `pending.enterpriseId` (#6156), so the dialog's existing success path
 * (dispatch `login`, reload) completes the switch. Cancelling the dialog clears
 * the step and leaves the caller on its current enterprise, with nothing
 * dispatched.
 *
 * The token arm is handed back for the CALLER to dispatch, rather than
 * dispatched here. Importing `login` into this module would make it the first
 * module to enter `slices/auth`, and `slices/auth` reaches `utils/store`
 * through `services/auth` → `utils/http` — so `store.ts` would read
 * `authSlice.reducer` while `slices/auth` was still evaluating, and the store
 * would come up with an undefined `auth` reducer (every suite that mounts the
 * store then dies at import). Callers already import `login` themselves, and
 * in a position where `services/auth` is evaluated first.
 *
 * Consume the outcome with `onSwitchCompleted`, which runs a caller's work on
 * the token arm only: the page stays put for the step arm's dialog.
 */
export async function switchEnterpriseWithMfa(
  enterpriseId: string,
  dispatch: AppDispatch,
): Promise<EnterpriseSwitchOutcome> {
  const res: SwitchEnterpriseResponse | EnterpriseMfaStep =
    await switchEnterprise(enterpriseId);

  if (isEnterpriseMfaStep(res)) {
    dispatch(openEnterpriseMfaSwitch(mfaOptionsOf(res)));
    return { mfaRequired: true };
  }

  return { mfaRequired: false, token: res.token };
}

/**
 * The one place a switch outcome is turned into work.
 *
 * A switch either mints a session, or parks the MFA step. On the step arm the
 * switch has not happened yet — `AuthenticationDialog` is open on its TOTP step
 * and completes it — so reloading the page, recording `switch_enterprise`, or
 * acting on the session would run against the enterprise the user is still on.
 * Every consumer passes its own follow-up work through here, so the arm is
 * decided once instead of re-derived at each call site.
 *
 * Returns whatever `onSwitched` returned, or `undefined` on the step arm.
 */
export function onSwitchCompleted<T>(
  outcome: EnterpriseSwitchOutcome,
  handlers: {
    onSwitched: (token: string) => T;
    onMfaStep?: () => void;
  },
): T | undefined {
  if (outcome.mfaRequired) {
    handlers.onMfaStep?.();
    return undefined;
  }
  return handlers.onSwitched(outcome.token);
}
